/**
 * Sort — the inbox of charges that still need a category.
 *
 * The whole design is about making the common case one click. If a sorting rule
 * or a known merchant suggests a category, the suggestion is a button: press it
 * and the charge is filed, with undo in the toast. Anything less obvious falls
 * through to the picker. Reaching zero is the point, so the empty state is a
 * reward rather than a shrug.
 */

import { useState } from 'react';
import {
  extractMerchantKeyword,
  formatMoney,
  merchantName,
  type BudgetItem,
  type Transaction,
} from '@alohamula/domain';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '../components/AppShell';
import { Button, EmptyState, Section } from '../components/ui';
import { useToast } from '../components/ui/ToastProvider';
import { TransactionRow } from '../components/TransactionRow';
import { CategorizeDialog } from '../components/CategorizeDialog';
import {
  useCreateSortingRule,
  useRestoreTransaction,
  useSetAllocations,
  useTrashTransaction,
} from '../api/queries';
import type { Budget } from '../budget/useBudget';
import { CheckIcon, SplitIcon, TrashIcon } from '../components/icons';
import './pages.css';

interface SortPageProps {
  budget: Budget;
  now: Date;
}

export function SortPage({ budget, now }: SortPageProps) {
  const navigate = useNavigate();
  const { uncategorized, suggestions, itemsById, index, envelopes, bills } = budget;
  const [editing, setEditing] = useState<Transaction | null>(null);
  const [remember, setRemember] = useState(true);

  const setAllocations = useSetAllocations();
  const trash = useTrashTransaction();
  const restore = useRestoreTransaction();
  const createRule = useCreateSortingRule();
  const toast = useToast();

  const busy = setAllocations.isPending || trash.isPending;

  /**
   * Files a charge, then offers undo. `learnFrom` is the raw bank description a
   * new sorting rule should be built from — only passed when the user chose the
   * category themselves, since learning from a suggestion teaches nothing.
   */
  async function file(
    transaction: Transaction,
    allocations: { budgetItemId: string; amount: number }[],
    learnFrom?: string
  ) {
    const result = await setAllocations.mutateAsync({
      transactionId: transaction.id,
      allocations,
      expectedVersion: transaction.version,
    });

    const single =
      allocations.length === 1 ? itemsById.get(allocations[0].budgetItemId) : undefined;

    toast.showUndo(
      single ? `Filed under ${single.name}` : `Split across ${allocations.length} categories`,
      () => {
        // Undoing a filing means going back to uncategorized, and the write we
        // just made bumped the version.
        setAllocations.mutate({
          transactionId: transaction.id,
          allocations: [],
          expectedVersion: result.transactionVersion,
        });
      }
    );

    // Teaching the app happens as a side effect of sorting, never as a chore.
    // Only the unambiguous single-category case becomes a rule.
    if (remember && single && learnFrom) {
      const keyword = extractMerchantKeyword(learnFrom);
      if (keyword) {
        createRule.mutate({
          keyword,
          budgetItemId: single.id,
          createdFromDescription: learnFrom,
        });
      }
    }
  }

  async function moveToTrash(transaction: Transaction) {
    const trashed = await trash.mutateAsync({
      id: transaction.id,
      expectedVersion: transaction.version,
    });
    toast.showUndo('Moved to trash', () => {
      restore.mutate({ id: transaction.id, expectedVersion: trashed.version });
    });
  }

  const total = uncategorized.reduce(
    (sum, transaction) => sum + Math.abs(transaction.amount),
    0
  );

  return (
    <>
      <PageHeader
        title="Sort"
        period={budget.period}
        action={
          <div className="row" style={{ gap: 'var(--space-sm)', flexWrap: 'wrap' }}>
            {uncategorized.length > 0 && (
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(event) => setRemember(event.target.checked)}
                />
                Remember my choices as rules
              </label>
            )}
            <Button size="sm" emphasis="ghost" onClick={() => navigate('/settings')}>
              Sorting rules
            </Button>
          </div>
        }
      />

      <button
        type="button"
        className="card headline"
        onClick={() => navigate('/transactions?filter=filed')}
        aria-label={`${budget.transactions.length - uncategorized.length} of ${budget.transactions.length} categorized. Show filed charges.`}
      >
        <div className="headline__figure">
          <span className="headline__label">Categorized</span>
          <span className="headline__value">
            {budget.transactions.length - uncategorized.length} of {budget.transactions.length}
          </span>
          <span className="headline__note">Tap to see every filed charge</span>
        </div>
      </button>

      {uncategorized.length === 0 ? (
        <div className="card">
          <EmptyState
            title="Everything is sorted"
            body={
              budget.transactionsInPeriod.length === 0
                ? 'No charges have landed this period yet.'
                : 'Every charge has a category, so the envelope balances are accurate.'
            }
            action={
              <Button onClick={() => navigate('/transactions?filter=filed')}>
                See categorized charges
              </Button>
            }
          />
        </div>
      ) : (
        <Section title={`${uncategorized.length} to sort`} meta={formatMoney(total)}>
          <ul className="card tx-list">
            {uncategorized.map((transaction) => {
              const suggestion = suggestions.get(transaction.id);
              const suggested = suggestion ? itemsById.get(suggestion.budgetItemId) : undefined;

              return (
                <TransactionRow
                  key={transaction.id}
                  transaction={transaction}
                  index={index}
                  itemsById={itemsById}
                  now={now}
                  actions={
                    <>
                      {suggested && suggestion && (
                        <SuggestionButton
                          item={suggested}
                          reason={
                            suggestion.source === 'rule'
                              ? `Your rule for "${suggestion.matchedKeyword}"`
                              : `Matched "${suggestion.matchedKeyword}"`
                          }
                          disabled={busy}
                          onClick={() =>
                            file(transaction, [
                              { budgetItemId: suggested.id, amount: Math.abs(transaction.amount) },
                            ])
                          }
                        />
                      )}

                      <Button size="sm" disabled={busy} onClick={() => setEditing(transaction)}>
                        <SplitIcon width={16} height={16} />
                        {suggested ? 'Somewhere else' : 'Choose a category'}
                      </Button>

                      <Button
                        size="sm"
                        emphasis="ghost"
                        aria-label={`Move ${merchantName(transaction.description)} to trash`}
                        disabled={busy}
                        onClick={() => void moveToTrash(transaction)}
                      >
                        <TrashIcon width={16} height={16} />
                      </Button>
                    </>
                  }
                />
              );
            })}
          </ul>
        </Section>
      )}

      <CategorizeDialog
        transaction={editing}
        envelopes={envelopes}
        bills={bills}
        busy={setAllocations.isPending}
        onClose={() => setEditing(null)}
        onSubmit={(allocations) => {
          const transaction = editing;
          if (!transaction) return;
          setEditing(null);
          void file(transaction, allocations, transaction.description);
        }}
      />
    </>
  );
}

/**
 * The one-click path. The reason lives in the tooltip and the aria-label rather
 * than on screen: the button's own label is the category, which is the thing
 * being decided.
 */
function SuggestionButton({
  item,
  reason,
  disabled,
  onClick,
}: {
  item: BudgetItem;
  reason: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <Button
      emphasis="primary"
      size="sm"
      disabled={disabled}
      onClick={onClick}
      title={reason}
      aria-label={`File under ${item.name}. ${reason}`}
    >
      <CheckIcon width={16} height={16} />
      {item.name}
    </Button>
  );
}
